import { flow } from "fp-ts/lib/function";

import { DestinationTypeBm, type LensDestination, type MyLensesLensStatus } from "../../gql-generated/types";

import { publishedStatusEntry } from "./lensStatusMap";
import { LOCKED_BASE_STATUSES } from "./makeLensActionsConfigBasedOnStatus";

const makeProfileText = (destinations: readonly LensDestination[]) => (destinationText: string) => {
    if (destinationText) {
        return destinationText;
    }

    const profileDestinations = destinations.filter(
        (destination) => destination.destinationType === DestinationTypeBm.DestinationTypePublicProfile,
    );

    return profileDestinations.length > 0
        ? profileDestinations.length === 1
            ? "To a Public Profile"
            : `To ${profileDestinations.length} Public Profiles`
        : destinationText;
};

const makesAdAccountText = (destinations: readonly LensDestination[]) => (destinationText: string) => {
    const adDestinations = destinations.filter(
        (destination) => destination.destinationType === DestinationTypeBm.DestinationTypeAdAccount,
    );

    const adAccountsText =
        adDestinations.length > 0
            ? adDestinations.length === 1
                ? "an Ad Account"
                : `${adDestinations.length} Ad Accounts`
            : "";

    if (adAccountsText) {
        return destinationText ? `${destinationText} and to ${adAccountsText}` : `To ${adAccountsText}`;
    }

    return destinationText;
};

const makeUserText = (destinations: readonly LensDestination[], userId: string) => (destinationText: string) => {
    const userDestination = destinations.find(
        (destination) => destination.destinationType === DestinationTypeBm.DestinationTypeUser,
    );

    if (userDestination) {
        return userDestination.id === userId ? "To Your Profile" : "To a Profile";
    }

    return destinationText;
};

export const makeDestinationsText = (
    destinations: readonly LensDestination[],
    userId: string,
    status: MyLensesLensStatus | undefined,
    displayStatusName: string,
): string => {
    if (destinations.length === 0 || (!!status && LOCKED_BASE_STATUSES.has(status))) {
        return "";
    }

    if (displayStatusName !== publishedStatusEntry.name) {
        return "See more...";
    }

    return flow(
        makeUserText(destinations, userId),
        makeProfileText(destinations),
        makesAdAccountText(destinations),
    )("");
};
